import React, { useState, useEffect, useCallback } from "react";
import login from "../media3/authlogin.png";
import signup from "../media3/authsignup.png";
import eye from "../media3/eye.svg";
import "../style/component/AuthModal.css";

function AuthModal({
  setIsModalOpen,
  setIsLoggedIn,
  setPaymentModalOpen,
  selectedSkin,
}) {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    dob: "",
    confirmPassword: "",
    rememberMe: false,
    agreeTerms: false,
  });
  const [message, setMessage] = useState({ text: "", type: "" });

  // Load users from localStorage once
  useEffect(() => {
    const storedUsers = localStorage.getItem("users");
    if (storedUsers) {
      try {
        setUsers(JSON.parse(storedUsers));
      } catch (err) {
        console.error("Failed to parse users from localStorage", err);
        setUsers([{ email: "test@example.com", password: "123456" }]);
      }
    } else {
      setUsers([{ email: "test@example.com", password: "123456" }]);
    }
  }, []);

  // Save users to localStorage whenever users array changes
  useEffect(() => {
    if (users.length > 0) {
      localStorage.setItem("users", JSON.stringify(users));
    }
  }, [users]);

  // Check if already logged in (on mount)
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      setIsLoggedIn(true);
    }
  }, [setIsLoggedIn]);

  // Handle input change
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setMessage({ text: "", type: "" });
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setMessage({ text: "", type: "" });

      if (isLogin) {
        // === LOGIN LOGIC ===
        const user = users.find(
          (u) => u.email === formData.email && u.password === formData.password
        );

        if (user) {
          // Store auth info
          localStorage.setItem("authToken", "loggedIn");
          localStorage.setItem("userEmail", user.email);
          localStorage.setItem("username", user.name || user.username || "User");
          localStorage.setItem("email", user.email);
          localStorage.setItem("user", JSON.stringify(user));

          // Update parent state
          setIsLoggedIn(true);

          // Show success
          setMessage({ text: "Login successful!", type: "success" });

          // Close modal and open payment modal if needed
          setTimeout(() => {
            setIsModalOpen(false);
            if (selectedSkin && setPaymentModalOpen) {
              setPaymentModalOpen(true);
            }
          }, 1500);
        } else {
          setMessage({ text: "Invalid email or password.", type: "error" });
        }
      } else {
        // === SIGNUP LOGIC ===
        const { name, email, password, confirmPassword, agreeTerms } = formData;

        if (!name || !email || !password) {
          setMessage({ text: "Please fill all required fields.", type: "error" });
          return;
        }

        if (password !== confirmPassword) {
          setMessage({ text: "Passwords do not match.", type: "error" });
          return;
        }

        if (!agreeTerms) {
          setMessage({ text: "Please agree to terms and privacy policy.", type: "error" });
          return;
        }

        if (users.some((u) => u.email === email)) {
          setMessage({ text: "Email already registered.", type: "error" });
          return;
        }

        // Add new user
        const newUser = { email, password, name };
        setUsers((prev) => [...prev, newUser]);

        // Success
        setMessage({
          text: "Signup successful! Now login with your credentials.",
          type: "success",
        });

        // Switch to login
        setIsLogin(true);

        // Reset form
        setFormData({
          email: "",
          password: "",
          name: "",
          phone: "",
          dob: "",
          confirmPassword: "",
          rememberMe: false,
          agreeTerms: false,
        });
      }
    },
    [isLogin, formData, users, selectedSkin, setIsLoggedIn, setIsModalOpen, setPaymentModalOpen]
  );

  // Toggle login/signup
  const toggleMode = () => {
    setIsLogin((prev) => !prev);
    setMessage({ text: "", type: "" });
    setFormData((prev) => ({
      ...prev,
      email: "",
      password: "",
      name: "",
      phone: "",
      dob: "",
      confirmPassword: "",
    }));
  };

  return (
    <div className="auth-modal-overlay">
      <div className="auth-modal-card">
        <button
          className="auth-close-btn"
          onClick={() => setIsModalOpen(false)}
          aria-label="Close modal"
        >
          X
        </button>

        <div className="auth-modal-left">
          <img
            src={isLogin ? login : signup}
            alt={isLogin ? "Login" : "Signup"}
            className="auth-modal-image"
          />
        </div>

        <div className="auth-modal-right">
          <div className="auth-modal-content">
            <h1 className="auth-modal-title">
              {isLogin ? (
                <>
                  Hi, Welcome!<span className="auth-wave-emoji"></span>
                </>
              ) : (
                "Sign Up"
              )}
            </h1>

            <form onSubmit={handleSubmit} className="auth-modal-form">
              {isLogin ? (
                <>
                  {/* ========== LOGIN FORM ========== */}
                  <div className="auth-form-group">
                    <p className="auth-toggle-text">
                      Don't have an account?{" "}
                      <span className="auth-toggle-link" onClick={toggleMode}>
                        Sign Up now
                      </span>
                    </p>

                    <label className="auth-form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="example@gmail.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="auth-form-input"
                      required
                    />
                  </div>

                  <div className="auth-form-group">
                    <label className="auth-form-label">Password</label>
                    <div className="auth-password-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="@#*%"
                        value={formData.password}
                        onChange={handleChange}
                        className="auth-form-input"
                        required
                      />
                      <button
                        type="button"
                        className="auth-password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label="Toggle password visibility"
                      >
                        <span className="auth-eye-icon">
                          <img src={eye} alt="" />
                        </span>
                      </button>
                    </div>
                  </div>

                  {message.text && (
                    <p className={`auth-form-message ${message.type}`}>
                      {message.text}
                    </p>
                  )}

                  <button type="submit" className="auth-modal-button">
                    Login
                  </button>
                </>
              ) : (
                <>
                  {/* ========== SIGNUP FORM ========== */}
                  <div className="auth-form-row">
                    <div className="auth-form-group">
                      <label className="auth-form-label">Name</label>
                      <input
                        type="text"
                        name="name"
                        placeholder="Enter your name"
                        value={formData.name}
                        onChange={handleChange}
                        className="auth-form-input"
                        required
                      />
                    </div>
                    <div className="auth-form-group">
                      <label className="auth-form-label">Phone No.</label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="1585545564"
                        value={formData.phone}
                        onChange={handleChange}
                        className="auth-form-input"
                      />
                    </div>
                  </div>

                  <div className="auth-form-row">
                    <div className="auth-form-group">
                      <label className="auth-form-label">Email</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="example@gmail.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="auth-form-input"
                        required
                      />
                    </div>
                    <div className="auth-form-group">
                      <label className="auth-form-label">
                        Date of birth (MM/DD/YY)
                      </label>
                      <input
                        type="text"
                        name="dob"
                        placeholder="07/22/2000"
                        value={formData.dob}
                        onChange={handleChange}
                        className="auth-form-input"
                      />
                    </div>
                  </div>

                  <div className="auth-form-row">
                    <div className="auth-form-group">
                      <label className="auth-form-label">Password</label>
                      <div className="auth-password-wrapper">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          placeholder="@134"
                          value={formData.password}
                          onChange={handleChange}
                          className="auth-form-input"
                          required
                        />
                        <button
                          type="button"
                          className="auth-password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <span className="auth-eye-icon">
                            <img src={eye} alt="" />
                          </span>
                        </button>
                      </div>
                    </div>
                    <div className="auth-form-group">
                      <label className="auth-form-label">Confirm password</label>
                      <div className="auth-password-wrapper">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          placeholder="@134"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          className="auth-form-input"
                          required
                        />
                        <button
                          type="button"
                          className="auth-password-toggle"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                        >
                          <span className="auth-eye-icon">
                            <img src={eye} alt="" />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="auth-form-checkboxes">
                    <label className="auth-checkbox-label">
                      <input
                        type="checkbox"
                        name="rememberMe"
                        checked={formData.rememberMe}
                        onChange={handleChange}
                      />
                      <span className="auth-checkbox-text">Remember me</span>
                    </label>
                    <label className="auth-checkbox-label">
                      <input
                        type="checkbox"
                        name="agreeTerms"
                        checked={formData.agreeTerms}
                        onChange={handleChange}
                        required
                      />
                      <span className="auth-checkbox-text">
                        I agree to Terms & Policy
                      </span>
                    </label>
                  </div>

                  {message.text && (
                    <p className={`auth-form-message ${message.type}`}>
                      {message.text}
                    </p>
                  )}

                  <button type="submit" className="auth-modal-button">
                    Sign Up
                  </button>

                  <p className="auth-toggle-text">
                    Have an account?{" "}
                    <span className="auth-toggle-link" onClick={toggleMode}>
                      Log in
                    </span>
                  </p>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthModal;